import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { FormModal } from "@/components/ui/form-modal"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { UserPlus } from "lucide-react"
import { useCreateAccessRequest } from "@/hooks/useAccessRequests"

interface SolicitarAcessoModalProps {
  children?: React.ReactNode
  defaultType?: string
}

const SolicitarAcessoModal = ({ children, defaultType }: SolicitarAcessoModalProps) => {
  const [formData, setFormData] = useState({
    type: defaultType || "",
    person_company: "",
    document: "",
    scheduled_date: "",
    scheduled_time: "",
    observations: "",
    recurrence: "nenhuma"
  })
  const [open, setOpen] = useState(false)

  const createAccessRequest = useCreateAccessRequest()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.type || !formData.person_company || !formData.scheduled_date || !formData.scheduled_time) {
      return
    }

    try {
      await createAccessRequest.mutateAsync({
        type: formData.type as any,
        person_company: formData.person_company,
        document: formData.document || undefined,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        observations: formData.observations || undefined,
        recurrence: formData.recurrence as any
      })
      
      setFormData({
        type: defaultType || "",
        person_company: "",
        document: "",
        scheduled_date: "",
        scheduled_time: "",
        observations: "",
        recurrence: "nenhuma"
      })
      setOpen(false)
    } catch (error) {
      console.error("Erro ao criar solicitação:", error)
    }
  }

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'visitante':
        return 'Visitante'
      case 'delivery':
        return 'Delivery'
      case 'servico':
        return 'Serviço'
      case 'mudancas':
        return 'Mudanças'
      case 'outros':
        return 'Outros'
      default:
        return 'Selecione o tipo'
    }
  }

  const formContent = (
    <>
      <div className="space-y-2">
        <Label htmlFor="type">Tipo de Acesso</Label>
        <Select 
          value={formData.type} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
        >
          <SelectTrigger className="border-2 border-blue-500 focus:border-blue-600 focus:ring-blue-500">
            <SelectValue placeholder="Selecione o tipo" />
          </SelectTrigger>
          <SelectContent className="bg-background border border-border shadow-lg z-50">
            <SelectItem value="visitante">👤 Visitante</SelectItem>
            <SelectItem value="delivery">📦 Delivery</SelectItem>
            <SelectItem value="servico">🔧 Serviço</SelectItem>
            <SelectItem value="mudancas">📦 Mudanças</SelectItem>
            <SelectItem value="outros">🏢 Outros</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="person_company">Pessoa/Empresa</Label>
        <Input
          id="person_company"
          placeholder="Nome da pessoa ou empresa"
          value={formData.person_company}
          onChange={(e) => setFormData(prev => ({ ...prev, person_company: e.target.value }))}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="document">Documento</Label>
        <Input
          id="document"
          placeholder="CPF, RG ou CNPJ (opcional)"
          value={formData.document}
          onChange={(e) => setFormData(prev => ({ ...prev, document: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="scheduled_date">Data</Label>
          <Input
            id="scheduled_date"
            type="date"
            value={formData.scheduled_date}
            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="scheduled_time">Horário</Label>
          <Input
            id="scheduled_time"
            type="time"
            value={formData.scheduled_time}
            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="recurrence">Recorrência</Label>
        <Select 
          value={formData.recurrence} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, recurrence: value }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nenhuma">Nenhuma</SelectItem>
            <SelectItem value="semanalmente">Semanalmente</SelectItem>
            <SelectItem value="mensalmente">Mensalmente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="observations">Observações</Label>
        <Textarea
          id="observations"
          placeholder="Observações adicionais (opcional)"
          value={formData.observations}
          onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
          rows={3}
        />
      </div>
    </>
  )

  return (
    <FormModal
      title="Solicitar Liberação de Acesso"
      description="Preencha os dados para solicitar a liberação de acesso"
      onSubmit={handleSubmit}
      submitButtonText="Criar Solicitação"
      loading={createAccessRequest.isPending}
      open={open}
      onOpenChange={setOpen}
      formContent={formContent}
    >
      {children || (
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          Solicitar Acesso
        </Button>
      )}
    </FormModal>
  )
}

export default SolicitarAcessoModal